import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader } from "@shared/components/ui/card";
import { Badge } from "@shared/components/ui/badge";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@shared/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import {
  Package,
  Truck,
  Search,
  Filter,
  RefreshCw,
  MapPin,
  User,
  Phone,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { toast } from "sonner";
import { useAuth } from "@features/auth/contexts/AuthContext";
import useTitle from "@shared/hooks/useTitle";
import HeadLayout from "@shared/layouts/HeadLayout";
import ThreeDotLoader from "@shared/components/ThreeDotLoader";
import EnhancedLivreurStatusManager from "./EnhancedLivreurStatusManager";
import EnhancedPickupStatusManager from "./EnhancedPickupStatusManager";
import SpeedafOrderButton from "./SpeedafOrderButton";
import * as packagesService from "../services/packagesService";
import {
  getStatusCssClass,
  formatStatusName,
} from "@shared/utils/statusColors";

const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "status.allPackages" },
  { value: "assigned", label: "status.assigned" },
  { value: "collected", label: "status.collected" },
  { value: "out_for_delivery", label: "status.out_for_delivery" },
  { value: "delivered", label: "status.delivered" },
  { value: "delivery_attempted", label: "status.delivery_attempted" },
  { value: "in_transit", label: "status.in_transit" },
  { value: "pending", label: "status.pending" },
];

function EnhancedPackageCard({ pkg, onUpdate }) {
  const { t } = useTranslation();
  const { user } = useAuth();

  const getStatusIcon = (status) => {
    switch (status) {
      case "delivered":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "out_for_delivery":
        return <Truck className="h-4 w-4 text-blue-500" />;
      case "delivery_attempted":
        return <AlertCircle className="h-4 w-4 text-orange-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  return (
    <Card className="group hover:shadow-lg transition-all duration-300 border-l-4 border-l-blue-500">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              {getStatusIcon(pkg.status)}
              <Badge className={getStatusCssClass(pkg.status)}>
                {formatStatusName(pkg.status)}
              </Badge>
              {pkg.trackingNumber && (
                <Badge variant="outline" className="text-xs">
                  {pkg.trackingNumber}
                </Badge>
              )}
            </div>
            <div className="text-sm text-muted-foreground">
              #{pkg._id} • {format(new Date(pkg.createdAt), "MMM dd, HH:mm")}
            </div>
          </div>
          <div className="flex gap-2">
            {user?.brandName === "speedaf" && (
              <SpeedafOrderButton
                packageData={pkg}
                onSuccess={onUpdate}
                variant="outline"
                size="sm"
              />
            )}
            <EnhancedLivreurStatusManager
              packageData={pkg}
              onUpdate={onUpdate}
              trigger={
                <Button size="sm" variant="outline">
                  {t("livreur.updateStatus")}
                </Button>
              }
            />
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Customer Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{pkg.customer.name}</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <span className="font-mono text-sm">{pkg.customer.phone}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">
                {pkg.customer.address}, {pkg.customer.city}
                {pkg.customer.secteur && ` - ${pkg.customer.secteur}`}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-sm">
              <span className="font-medium text-muted-foreground">
                {t("livreur.totalAmount")}:
              </span>
              <span className="ml-2 font-bold text-lg">
                {pkg.totalAmount} MAD
              </span>
            </div>
            {pkg.statusHistory?.length > 0 && (
              <div className="text-sm">
                <span className="font-medium text-muted-foreground">
                  {t("livreur.lastUpdate")}:
                </span>
                <span className="ml-2">
                  {format(
                    new Date(
                      pkg.statusHistory[pkg.statusHistory.length - 1].date
                    ),
                    "MMM dd, HH:mm"
                  )}
                </span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function EnhancedPickupCard({ pickup, onUpdate }) {
  const { t } = useTranslation();

  return (
    <Card className="group hover:shadow-lg transition-all duration-300 border-l-4 border-l-green-500">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Badge className={getStatusCssClass(pickup.status)}>
                {formatStatusName(pickup.status)}
              </Badge>
            </div>
            <div className="text-sm text-muted-foreground">
              #{pickup._id} •{" "}
              {format(new Date(pickup.createdAt), "MMM dd, HH:mm")}
            </div>
          </div>
          <EnhancedPickupStatusManager
            pickupData={pickup}
            onUpdate={onUpdate}
            trigger={
              <Button size="sm" variant="outline">
                {t("livreur.updateStatus")}
              </Button>
            }
          />
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{pickup.name || "No name"}</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <span className="font-mono text-sm">
                {pickup.phone || "No phone"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">{pickup.address || "No address"}</span>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Package className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">
                {pickup.type === "product"
                  ? pickup.products?.length || 0
                  : pickup.packages?.length || 0}{" "}
                {pickup.type === "product"
                  ? t("livreur.products")
                  : t("livreur.packages")}
              </span>
            </div>
            <div className="text-sm">
              <span className="font-medium text-muted-foreground">
                {t("livreur.type")}:
              </span>
              <span className="ml-2 capitalize">
                {pickup.type || "package"}
              </span>
            </div>
            {pickup.note && (
              <div className="text-sm">
                <span className="font-medium text-muted-foreground">
                  {t("livreur.note")}:
                </span>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                  {pickup.note}
                </p>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ComprehensiveLivreurDashboard() {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("packages");

  useTitle(t("livreur.dashboard"));

  // Fetch packages assigned to livreur
  const {
    data: packagesData,
    isLoading: packagesLoading,
    error: packagesError,
    refetch: refetchPackages,
  } = useQuery({
    queryKey: ["livreur-packages", statusFilter],
    queryFn: () => {
      const params =
        statusFilter !== "all" ? `status=${statusFilter}&limit=50` : "limit=50";
      return packagesService.getLivreurPackages(params);
    },
    select: (data) => data?.packages || [],
  });

  // Fetch pickups assigned to livreur
  const {
    data: pickupsData,
    isLoading: pickupsLoading,
    error: pickupsError,
    refetch: refetchPickups,
  } = useQuery({
    queryKey: ["livreur-pickups", statusFilter],
    queryFn: () => {
      const params =
        statusFilter !== "all" ? `status=${statusFilter}&limit=50` : "limit=50";
      return packagesService.getLivreurPickups(params);
    },
    select: (data) => data?.pickups || [],
  });

  const packages = packagesData || [];
  const pickups = pickupsData || [];

  // Filter data based on search term
  const filteredPackages = packages.filter(
    (pkg) =>
      pkg.customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pkg.customer.phone.includes(searchTerm) ||
      pkg.customer.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (pkg.trackingNumber &&
        pkg.trackingNumber.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const filteredPickups = pickups.filter(
    (pickup) =>
      (pickup.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (pickup.phone || "").includes(searchTerm) ||
      (pickup.address || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate statistics
  const getStatistics = () => {
    const packageStats = {
      total: packages.length,
      delivered: packages.filter((p) => p.status === "signed").length,
      outForDelivery: packages.filter((p) => p.status === "out_for_delivery")
        .length,
      attempted: packages.filter((p) => p.status === "delivery_attempted")
        .length,
    };

    const pickupStats = {
      total: pickups.length,
      pending: pickups.filter((p) => p.status === "pending").length,
      collected: pickups.filter((p) => p.status === "collected").length,
      delivered: pickups.filter((p) => p.status === "signed").length,
    };

    return { packageStats, pickupStats };
  };

  const { packageStats, pickupStats } = getStatistics();

  const handleRefresh = async () => {
    try {
      await Promise.all([refetchPackages(), refetchPickups()]);
      toast.success(t("livreur.dataRefreshedSuccessfully"));
    } catch {
      toast.error(t("livreur.failedToRefreshData"));
    }
  };

  const handleUpdate = () => {
    // Refresh data when items are updated
    refetchPackages();
    refetchPickups();
  };

  if (packagesError || pickupsError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <AlertCircle className="h-12 w-12 text-red-500" />
        <div className="text-center">
          <h3 className="text-lg font-medium">
            {t("livreur.errorLoadingData")}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t("livreur.pleaseRefreshPage")}
          </p>
        </div>
        <Button onClick={handleRefresh} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          {t("livreur.retry")}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <HeadLayout title={t("livreur.dashboard")}>
        <Button onClick={handleRefresh} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          {t("livreur.refresh")}
        </Button>
      </HeadLayout>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Package className="h-8 w-8 text-blue-500" />
              <div>
                <div className="text-2xl font-bold">{packageStats.total}</div>
                <p className="text-sm text-muted-foreground">
                  {t("livreur.totalPackages")}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Truck className="h-8 w-8 text-yellow-500" />
              <div>
                <div className="text-2xl font-bold">
                  {packageStats.outForDelivery}
                </div>
                <p className="text-sm text-muted-foreground">
                  {t("livreur.outForDelivery")}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-green-500" />
              <div>
                <div className="text-2xl font-bold">
                  {packageStats.delivered}
                </div>
                <p className="text-sm text-muted-foreground">
                  {t("livreur.deliveredToday")}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-8 w-8 text-purple-500" />
              <div>
                <div className="text-2xl font-bold">{pickupStats.total}</div>
                <p className="text-sm text-muted-foreground">
                  {t("livreur.totalPickups")}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t("livreur.searchByCustomerPhoneCity")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_FILTER_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {t(option.label)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="packages" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            {t("livreur.packages")} ({filteredPackages.length})
          </TabsTrigger>
          <TabsTrigger value="pickups" className="flex items-center gap-2">
            <Truck className="h-4 w-4" />
            {t("livreur.pickups")} ({filteredPickups.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="packages" className="space-y-4">
          {packagesLoading ? (
            <div className="flex justify-center py-12">
              <ThreeDotLoader />
            </div>
          ) : filteredPackages.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">
                  {t("livreur.noPackagesFound")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {searchTerm
                    ? t("livreur.tryAdjustingSearch")
                    : t("livreur.noPackagesAssignedYet")}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredPackages.map((pkg) => (
                <EnhancedPackageCard
                  key={pkg._id}
                  pkg={pkg}
                  onUpdate={handleUpdate}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="pickups" className="space-y-4">
          {pickupsLoading ? (
            <div className="flex justify-center py-12">
              <ThreeDotLoader />
            </div>
          ) : filteredPickups.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Truck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">
                  {t("livreur.noPickupsFound")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {searchTerm
                    ? t("livreur.tryAdjustingSearch")
                    : t("livreur.noPickupsAssignedYet")}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredPickups.map((pickup) => (
                <EnhancedPickupCard
                  key={pickup._id}
                  pickup={pickup}
                  onUpdate={handleUpdate}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
